import { useState, useEffect } from 'react';
import { Stack, Group, Button, Text, Paper, Badge, Autocomplete } from '../../../components/gab/GabComponents';
import { useDebouncedValue } from '../../../components/gab/GabHooks';
import { useGeolocation } from '../lib/useGeolocation';
import { getCountyFromCoords, reverseGeocode, geocodeAddress } from '../lib/gis';
import { WORKER_URL } from '../../../lib/api-switch';

const WORKER = WORKER_URL;

export interface LocationValue {
  lat?: number;
  lng?: number;
  address?: string;
  street?: string;
  city?: string;
  state?: string;
  county?: string;
  fipsCode?: string;
  zip?: string;
}

interface Props {
  value?: LocationValue;
  onChange: (loc: LocationValue) => void;
}

interface Suggestion {
  value: string;
  lat: number;
  lng: number;
  county: string;
  state: string;
  city: string;
  zip: string;
}

export default function LocationInput({ value = {}, onChange }: Props) {
  const { getCurrentPosition, loading: gpsLoading } = useGeolocation();
  const [geocoding, setGeocoding] = useState(false);
  const [addressQuery, setAddressQuery] = useState(value.address || '');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [debouncedQuery] = useDebouncedValue(addressQuery, 400);

  useEffect(() => {
    if (!debouncedQuery || typeof debouncedQuery !== 'string' || debouncedQuery.length < 4) { setSuggestions([]); return; }
    let cancelled = false;
    fetch(`${WORKER}/api/nominatim/search?q=${encodeURIComponent(debouncedQuery)}&format=json&limit=5&addressdetails=1&countrycodes=us`, {
      headers: { 'Accept': 'application/json' }
    })
      .then(r => r.json())
      .then(data => {
        if (!cancelled) {
          setSuggestions(data.map((d: Record<string, unknown>) => ({
            value: d.display_name as string,
            lat: parseFloat(d.lat as string),
            lng: parseFloat(d.lon as string),
            county: ((d.address as Record<string, string>)?.county || '') as string,
            state: ((d.address as Record<string, string>)?.state || '') as string,
            city: ((d.address as Record<string, string>)?.city || (d.address as Record<string, string>)?.town || (d.address as Record<string, string>)?.village || '') as string,
            zip: ((d.address as Record<string, string>)?.postcode || '') as string,
          })));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  useEffect(() => {
    if (value.address && value.address !== addressQuery) {
      setAddressQuery(value.address);
    }
  }, [value.address]);

  const handleGps = async () => {
    try {
      const pos = await getCurrentPosition();
      const [county, address] = await Promise.all([
        getCountyFromCoords(pos.lat, pos.lng),
        reverseGeocode(pos.lat, pos.lng),
      ]);
      onChange({
        ...value,
        lat: pos.lat,
        lng: pos.lng,
        address: address?.address || value.address || '',
        street: address?.street || '',
        city: address?.city || '',
        state: county?.state || address?.state || '',
        county: county?.county || address?.county || '',
        fipsCode: county?.fipsCode || '',
        zip: address?.zip || '',
      });
    } catch (err) {
      console.error('GPS error:', err);
    }
  };

  const handleAddressLookup = async () => {
    if (!value.address) return;
    setGeocoding(true);
    try {
      const result = await geocodeAddress(value.address);
      if (result) {
        const county = await getCountyFromCoords(result.lat, result.lng);
        onChange({
          ...value,
          lat: result.lat,
          lng: result.lng,
          county: county?.county || result.county || value.county,
          fipsCode: county?.fipsCode || value.fipsCode,
          state: county?.state || value.state,
        });
      }
    } catch (err) {
      console.error('Geocode error:', err);
    }
    setGeocoding(false);
  };

  return (
    <Stack gap="sm">
      <Text fw={500} size="sm">Location</Text>

      <Group gap="sm">
        <Button
          leftSection={<i className="fa-solid fa-location-crosshairs" />}
          onClick={handleGps}
          loading={gpsLoading}
          variant="light"
          size="sm"
        >
          Use GPS
        </Button>
        {value.lat && (
          <Badge variant="light" color="var(--color-success)" size="sm">
            <i className="fa-solid fa-check" style={{ marginRight: 4 }} />
            {value.lat.toFixed(4)}, {value.lng?.toFixed(4)}
          </Badge>
        )}
      </Group>

      <Autocomplete
        placeholder="Start typing an address..."
        value={addressQuery}
        onChange={(val) => {
          const str = typeof val === 'string' ? val : '';
          setAddressQuery(str);
          onChange({ ...value, address: str });
        }}
        onOptionSubmit={(val) => {
          const selected = suggestions.find(s => s.value === val);
          if (selected) {
            setAddressQuery(selected.value);
            getCountyFromCoords(selected.lat, selected.lng).then(countyData => {
              onChange({
                ...value,
                address: selected.value,
                lat: selected.lat,
                lng: selected.lng,
                county: countyData?.county || selected.county || '',
                fipsCode: countyData?.fipsCode || '',
                state: countyData?.state || selected.state || '',
                city: selected.city || '',
                zip: selected.zip || '',
              });
            }).catch(() => {});
          }
        }}
        data={[...new Set(suggestions.map(s => s.value))]}
        rightSection={
          <Button
            size="compact-xs"
            variant="subtle"
            onClick={handleAddressLookup}
            loading={geocoding}
            style={{ marginRight: 4 }}
          >
            <i className="fa-solid fa-magnifying-glass" />
          </Button>
        }
        limit={5}
      />

      {(value.county || value.fipsCode) && (
        <Paper withBorder p="xs" radius="sm">
          <Group gap="lg">
            {value.county && <Text size="xs"><Text span fw={600}>County:</Text> {value.county}</Text>}
            {value.fipsCode && <Text size="xs"><Text span fw={600}>FIPS:</Text> {value.fipsCode}</Text>}
            {value.state && <Text size="xs"><Text span fw={600}>State:</Text> {value.state}</Text>}
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
